import { useEffect, useState } from 'react';
import { Avatar, Button, Drawer, Modal, Popconfirm, Progress, Spin, Tabs, Tag, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  CommentOutlined, DollarOutlined, FileTextOutlined, TeamOutlined,
  ExpandAltOutlined, EditFilled, DeleteFilled, ReloadOutlined, HistoryOutlined,
  CloseOutlined, CopyOutlined, LinkOutlined,
} from '@ant-design/icons';
import { useSelector } from '@legendapp/state/react';
import { state$ } from '../../state/globalState';
import { affairesState } from '../../state/actions/affairesState';
import { catchError } from '../../services/DaoService';
import { copyLinkToClipboard } from '../../helpers/clipboard';
import { AffaireService } from '../../services/AffaireService';
import { EtapeService } from '../../services/EtapeService';
import { TacheService } from '../../services/TacheService';
import { useUserContext } from '../../contexts/UserContext';
import Toast from '../../helpers/Toast';
import AffaireDetails from './AffaireDetails';
import AffaireCloseConfirmDrawer from './AffaireCloseConfirmDrawer';
import { InlineEditableInput } from '../utils/InlineEditableInput';
import Path from '../utils/Path';
import CommentaireFeed from '../commentaire/CommentaireFeed';
import PieceJointeList from '../piecejointe/PieceJointeList';
import TacheByAffaireList from '../tache/TacheByAffaireList';
import MembreAffaireByAffaireList from '../membreaffaire/MembreAffaireByAffaireList';
import AffaireLiensList from './AffaireLiensList';
import ActivityFeed from '../Activity/ActiviteFeed';
import LogTraces from '../logtrace/LogTraces';
import DepenseByAffaireList from '../depense/DepenseByAffaireList';
import AffaireEditForm from './AffaireEditForm';
import AiSummarySection from '../ai/AiSummarySection';
import Moment from 'react-moment';
import styles from './AffaireQuickView.module.scss';
import PropTypes from 'prop-types';

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return mobile;
}

export default function AffaireQuickView({ uid, mode = 'aside', onClose, onUpdate }) {
  const { check, currentUser } = useUserContext();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const effectiveMode = isMobile ? 'modal' : mode;

  const [localLoading, setLocalLoading] = useState(true);
  const [etapes, setEtapes] = useState([]);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [editVisible, setEditVisible] = useState(false);
  const [closeConfirmVisible, setCloseConfirmVisible] = useState(false);
  const [pendingStep, setPendingStep] = useState(null);

  const affaire = useSelector(() => state$.affaires.list.get().find(a => a.uid === uid));

  useEffect(() => {
    if (!uid) return;
    setLocalLoading(true);
    Promise.all([
      affairesState.getAffaire(uid),
      EtapeService.getInstance().findByAffaire(uid),
    ])
      .then(([, e]) => setEtapes(e.data))
      .catch(catchError)
      .finally(() => setLocalLoading(false));
  }, [uid]);

  useEffect(() => {
    setIsReadOnly(!!affaire?.done);
  }, [affaire]);

  const canManageAffaire = affaire?.responsable_id === currentUser?.id || affaire?.user_id === currentUser?.id;
  const canEditStep = canManageAffaire;
  const canSeeLogs = canManageAffaire;
  const canManageMembers = canManageAffaire;
  const canComment = !!affaire?.current_membre || canManageAffaire;
  const canAttach = canComment;

  const doChangeStep = async (step) => {
    try {
      const r = await AffaireService.getInstance().changeStep(affaire.uid, { etape_id: step.id });
      affairesState.updateAffaireInList(r.data);
      Toast.success(r.message);
      onUpdate?.(r.data);
    } catch (e) { catchError(e); }
  };

  const handleStepChange = async (step) => {
    // Si l'étape ciblée clôture le dossier, vérifier qu'il ne reste pas de
    // tâches non résolues avant de l'appliquer directement.
    if (step.pivot?.resolu) {
      try {
        const r = await TacheService.getInstance().search({ affaire_id: affaire.id, resolu: false });
        const unresolved = r.data.data ?? r.data ?? [];
        if (unresolved.length > 0) {
          setPendingStep(step);
          setCloseConfirmVisible(true);
          return;
        }
      } catch (e) { catchError(e); return; }
    }
    await doChangeStep(step);
  };

  const handleFieldChange = (value, fieldName, record) => {
    AffaireService.getInstance()
      .update(record.uid, { ...record, [fieldName]: value })
      .then(r => {
        affairesState.updateAffaireInList(r.data);
        onUpdate?.(r.data);
      })
      .catch(catchError);
  };

  const handleDelete = () => {
    affairesState.deleteAffaire(uid).then(() => onClose());
  };

  const goFullPage = () => {
    navigate(`/affaire/${uid}`);
    onClose();
  };

  const tabItems = affaire ? [
    {
      key: 'details',
      label: 'Détails',
      children: <AffaireDetails affaire={affaire} isReadOnly={isReadOnly} />,
    },
    check('VIEW-TACHES') && {
      key: 'taches',
      label: 'Tâches',
      children: <TacheByAffaireList isReadOnly={isReadOnly} canManage={canManageAffaire} affaire={affaire} />,
    },
    {
      key: 'commentaires',
      label: <span><CommentOutlined /> Commentaires</span>,
      children: <CommentaireFeed isReadOnly={isReadOnly} canCreate={canComment} parentId={affaire.id} parentType={AffaireService.CLASS_NAME} />,
    },
    check('VIEW-MEMBRE-AFFAIRES') && {
      key: 'collaborateurs',
      label: <span><TeamOutlined /> Collaborateurs</span>,
      children: <MembreAffaireByAffaireList isReadOnly={isReadOnly} canManage={canManageMembers} affaire={affaire} />,
    },
    {
      key: 'liens',
      label: <span><LinkOutlined /> Liens</span>,
      children: <AffaireLiensList affaire={affaire} canManage={canManageAffaire && !isReadOnly} />,
    },
    {
      key: 'documents',
      label: <span><FileTextOutlined /> Documents</span>,
      children: (
        <PieceJointeList
          parentId={affaire.id}
          parentType={AffaireService.CLASS_NAME}
          canAdd={canAttach}
          canRemove={canManageAffaire}
          canSee
        />
      ),
    },
    check('VIEW-DEPENSES') && {
      key: 'depenses',
      label: <span><DollarOutlined /> Dépenses</span>,
      children: <DepenseByAffaireList isReadOnly={isReadOnly} affaire={affaire} />,
    },
    {
      key: 'activite',
      label: <span><HistoryOutlined /> Activité</span>,
      children: <ActivityFeed parentId={affaire.id} parentType={AffaireService.CLASS_NAME} />,
    },
    canSeeLogs && {
      key: 'logs',
      label: <span><FileTextOutlined /> Logs</span>,
      children: <LogTraces target="affaire" parentId={affaire.id} />,
    },
  ].filter(Boolean) : [];

  const bodyContent = (
    <div className={styles.container}>
      {/* Bouton fermeture flottant */}
      <button className={styles.closeBtn} onClick={onClose} aria-label="Fermer">
        <CloseOutlined />
      </button>
      {localLoading && !affaire ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <Spin size="large" />
        </div>
      ) : affaire ? (
        <>
          {/* Priority band */}
          <div
            className={styles.priorityBand}
            style={{ background: affaire.priorite?.couleur ?? '#3E65AC' }}
          />

          {/* Header */}
          <div className={styles.header}>
            <div className={styles.tagsRow}>
              <Tooltip title="Copier le lien du dossier">
                <span
                  className={styles.codeTag}
                  onClick={() => copyLinkToClipboard(`/affaire/${affaire.uid}`, 'Lien du dossier')}
                  style={{ cursor: 'pointer' }}
                >
                  {affaire.code} <CopyOutlined style={{ fontSize: 11 }} />
                </span>
              </Tooltip>
              {affaire.modele?.nom && (
                <Tag color={affaire.modele.couleur} style={{ margin: 0 }}>{affaire.modele.nom}</Tag>
              )}
              {affaire.priorite?.nom && (
                <Tag color={affaire.priorite.couleur} style={{ margin: 0 }}>{affaire.priorite.nom}</Tag>
              )}
              {affaire.etape?.nom && (
                <Tag color="blue" style={{ margin: 0 }}>{affaire.etape.nom}</Tag>
              )}
              {!!affaire.done && <Tag color="default" style={{ margin: 0 }}>Clôturé</Tag>}
              {!!affaire.retard && !affaire.done && <Tag color="red" style={{ margin: 0 }}>En retard</Tag>}
            </div>

            <h2 className={styles.title}>
              <InlineEditableInput
                canEdit={canManageAffaire && !isReadOnly}
                type="text"
                fieldName="nom"
                value={affaire.nom}
                record={affaire}
                onChange={handleFieldChange}
              >
                {affaire.nom ?? '—'}
              </InlineEditableInput>
            </h2>

            <div className={styles.metaRow}>
              {affaire.responsable && (
                <span className={styles.metaItem}>
                  <Avatar size={18} src={affaire.responsable.avatar}>
                    {affaire.responsable.name?.[0]}
                  </Avatar>
                  <span>{affaire.responsable.name}</span>
                </span>
              )}
              {affaire.date_limite && (
                <span className={`${styles.metaItem} ${affaire.retard ? styles.metaRetard : ''}`}>
                  <Moment format="DD MMM YYYY">{affaire.date_limite}</Moment>
                </span>
              )}
              {affaire.progression != null && (
                <span className={styles.metaItem}>
                  {affaire.progression}% complété
                </span>
              )}
            </div>

            {affaire.progression != null && (
              <Progress
                percent={affaire.progression}
                size={['100%', 4]}
                showInfo={false}
                style={{ marginBottom: 8 }}
                strokeColor={affaire.priorite?.couleur ?? '#3E65AC'}
              />
            )}

            <div className={styles.actions}>
              <Tooltip title="Rafraîchir">
                <Button
                  type="text"
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => affairesState.getAffaire(uid)}
                />
              </Tooltip>
              {canManageAffaire && !isReadOnly && (
                <Button
                  type="primary"
                  size="small"
                  icon={<EditFilled />}
                  onClick={() => setEditVisible(true)}
                >
                  Éditer
                </Button>
              )}
              {canManageAffaire && !isReadOnly && (
                <Popconfirm
                  title="Supprimer ce dossier ?"
                  onConfirm={handleDelete}
                  okText="Supprimer"
                  okButtonProps={{ danger: true }}
                  cancelText="Annuler"
                >
                  <Button danger size="small" icon={<DeleteFilled />} />
                </Popconfirm>
              )}
              <Tooltip title="Ouvrir la page complète">
                <Button
                  type="text"
                  size="small"
                  icon={<ExpandAltOutlined />}
                  className={styles.openPageBtn}
                  onClick={goFullPage}
                >
                  {!isMobile && 'Page complète'}
                </Button>
              </Tooltip>
              <Tooltip title="Fermer">
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={onClose}
                />
              </Tooltip>
            </div>
          </div>

          {/* Résumé IA (section inline) */}
          <div className={styles.summaryInline}>
            <AiSummarySection type="affaire" uid={uid} />
          </div>

          {/* Étape progression */}
          {etapes.length > 0 && (
            <div className={styles.pathSection}>
              <Path
                readOnly={!canEditStep}
                currentStep={affaire.etape?.code}
                steps={etapes}
                onCurrentPathChange={handleStepChange}
              />
            </div>
          )}

          {/* Tabs */}
          <Tabs
            defaultActiveKey="details"
            items={tabItems}
            className={styles.tabs}
            size="small"
          />
        </>
      ) : null}
    </div>
  );

  const drawerWidth = Math.min(1000, window.innerWidth - 32);

  return (
    <>
      {effectiveMode === 'aside' ? (
        <Drawer
          open={!!uid}
          onClose={onClose}
          placement="right"
          width={drawerWidth}
          className={styles.drawer}
          title={null}
          closeIcon={null}
          destroyOnClose
        >
          {bodyContent}
        </Drawer>
      ) : (
        <Modal
          open={!!uid}
          onCancel={onClose}
          width={Math.min(1240, window.innerWidth - 32)}
          footer={null}
          title={null}
          closeIcon={null}
          className={styles.modal}
          destroyOnClose
        >
          {bodyContent}
        </Modal>
      )}

      {editVisible && affaire && (
        <AffaireEditForm
          visible={editVisible}
          uid={affaire.uid}
          onUpdate={async () => {
            setEditVisible(false);
            try {
              const res = await AffaireService.getInstance().find(uid);
              affairesState.updateAffaireInList(res.data);
              onUpdate?.(res.data);
            } catch (e) { catchError(e); }
          }}
          onCancel={() => setEditVisible(false)}
        />
      )}

      <AffaireCloseConfirmDrawer
        affaireId={affaire?.id}
        visible={closeConfirmVisible}
        onCancel={() => { setCloseConfirmVisible(false); setPendingStep(null); }}
        onConfirm={async () => {
          await doChangeStep(pendingStep);
          setCloseConfirmVisible(false);
          setPendingStep(null);
        }}
      />
    </>
  );
}

AffaireQuickView.propTypes = {
  uid: PropTypes.string,
  mode: PropTypes.oneOf(['aside', 'modal']),
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func,
};
